/**
 * MentionText — renders plain post/comment/message text with URLs, @mentions,
 * and #hashtags as clickable links. Mentions open the member's profile;
 * hashtags open the feed filtered by that tag.
 *
 * The API keeps mentions inside the body text, so each label is matched once,
 * here, against the people known for this context (`mentions`). The resolved
 * identity travels with the segment and drives navigation, so clicking never
 * depends on the label still matching a username.
 */
import { type MouseEvent, useMemo } from 'react';
import { Hashtag } from './Hashtag';
import { useMentionNavigate } from './richTextNavigation';
import type { MentionIndex, MentionMatch } from '@wlb/shared/utils/mentions';
import { parseRichText } from '../../utils/parseRichText';
import './richText.css';

export { parseRichText } from '../../utils/parseRichText';

interface MentionTextProps {
  text: string;
  /** Identities known for this post/comment/thread, from `buildMentionIndex`. */
  mentions?: MentionIndex;
}

export function MentionText({ text, mentions }: MentionTextProps) {
  const navigateToMention = useMentionNavigate();

  const segments = useMemo(() => parseRichText(text, mentions), [text, mentions]);

  const handleMentionClick = (
    match: MentionMatch,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    navigateToMention(match);
  };

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'mention') {
          const { userGuid, ambiguous, displayName } = segment.match;
          // Only a uniquely resolved mention is clickable; ambiguous or
          // unknown labels stay as plain text so they never navigate wrongly.
          if (!userGuid || ambiguous) {
            return <span key={index} className="mention-label">@{segment.value}</span>;
          }
          const label = displayName || segment.value;
          return (
            <button
              key={index}
              type="button"
              className="mention-link"
              onClick={event => handleMentionClick(segment.match, event)}
            >
              @{label}
            </button>
          );
        }
        if (segment.type === 'hashtag') {
          return <Hashtag key={index}>{segment.value}</Hashtag>;
        }
        if (segment.type === 'url') {
          return (
            <a
              key={index}
              className="text-link"
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={event => event.stopPropagation()}
            >
              {segment.value}
            </a>
          );
        }
        return <span key={index}>{segment.value}</span>;
      })}
    </>
  );
}
